import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BaseDataService, HttpResponsesHandlersFactory, IHttpResponseHandlerSettings } from "@bbwt/modules/data-service";
import { IQueryCommand, StringFilter } from "@features/filter";
import { IPagedData } from "@features/grid";
import { CoreRole } from "@main/roles";
import { setFieldsLocalTimeZone, setFiltersUTCTimeZone } from "@mipro/classes/date-utils";
import { SetLocalTimeZone, SetUTCTimeZone } from "@bbwt/utils";
import { SearchUserItem } from "@mipro/classes/search-user-item";
import { Slot } from "../models/slot";
import { EntityId } from "@bbwt/interfaces";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { PatientSuggestion } from "@project/models/patient-suggestion";
import { ClinicianSearchUseritem } from "../models/clinician-search-user-item";
import { SlotPage } from "../models/slot-page";
import { StringArrayFilter } from "@features/filter/classes/string-array-filter";

@Injectable({
    providedIn: "root"
})
export class ClinicSlotsService extends BaseDataService {
    public readonly url = "api/clinic-slots";
    public readonly entityTitle = "Clinic Slots";

    constructor(
        http: HttpClient,
        handlersFactory: HttpResponsesHandlersFactory,
        private appOnlineStateService: AppOnlineStateService) {
        super(http, handlersFactory);
    }

    create(item: Slot, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<Slot> {
        item.startDateTime = SetUTCTimeZone(item.startDateTime);
        item.endDateTime = SetUTCTimeZone(item.endDateTime);
        return this.handleRequest<Slot>(
            this.http.post<Slot>(`${this.url}`, item),
            this.handlersFactory.getForCreate("Slot", responseHandlerSettings)
        );
    }

    updateIsPaid(item: Slot, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<any> {
        return this.handleRequest<Slot>(
            this.http.put<Slot>(`${this.url}/set-paid/${item.id}`, null),
            this.handlersFactory.getForUpdate("Slot", responseHandlerSettings)
        );
    }

    update(item: Slot, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<any> {
        return this.handleRequest<Slot>(
            this.http.put<Slot>(`${this.url}`, item),
            this.handlersFactory.getForUpdate("Slot", responseHandlerSettings)
        );
    }

    async getPage(queryCommand: IQueryCommand, responseHandlerSettings?: IHttpResponseHandlerSettings, saveInCache: boolean = false): Promise<IPagedData<SlotPage>> {
        setFiltersUTCTimeZone(queryCommand, "startDate", "endDate");

        let page : IPagedData<SlotPage>;

        if(this.appOnlineStateService.isAppOnline) {
            page = await this.handleRequest(
                this.http.get<IPagedData<SlotPage>>(`${this.url}/page`, { params: this.constructHttpParams(queryCommand) }),
                this.handlersFactory.getForReadByFilter(responseHandlerSettings));

            if(saveInCache){
                // Save asynchronously in the cache for offline use
                this.getPageForOffline(queryCommand, responseHandlerSettings);
            }
        } else {
            page = await this.getPageForOffline(queryCommand, responseHandlerSettings);
        }

        (page?.items ?? []).forEach(item => {
            setFieldsLocalTimeZone(item, "startDateTime", "endDateTime");
        });

        return page;
    }

    async getPageForOffline(queryCommand: IQueryCommand, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<IPagedData<SlotPage>> {
        let page : IPagedData<SlotPage> = { total: 0, items: []};

        const queryCommandForOffline = {
            filters:
            [
                queryCommand.filters.find(x => x.propertyName == "startDate"),
                queryCommand.filters.find(x => x.propertyName == "endDate")
            ]
        }

        try{
            page = await this.handleRequest(
                this.http.get<IPagedData<SlotPage>>(`${this.url}/page-offline`, { params: this.constructHttpParams(queryCommandForOffline) }),
                this.handlersFactory.getForReadByFilter(responseHandlerSettings)
            );
        } catch {
            page = { total: 0, items: []};
        }

        const locationFilter = queryCommand.filters.find(x => x.propertyName == "appointmentLocationId") as StringFilter;
        const usersIdFilter = queryCommand.filters.find(x => x.propertyName == "usersIdList") as StringArrayFilter;

        page.items = page.items.filter(s =>
            (locationFilter == null || s.locationId == locationFilter.value) &&
            (usersIdFilter == null || usersIdFilter?.value?.includes(s.clinicianUserId)));

        return page;
    }

    async getCurrentPatientAppointments(queryCommand: IQueryCommand, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<Slot[]> {
        const slots = await this.handleRequest(
            this.http.get<Slot[]>(`${this.url}/patient-appointments`, { params: this.constructHttpParams(queryCommand) }),
            this.handlersFactory.getForReadByFilter(responseHandlerSettings)
        );

        (slots ?? []).forEach(item => {
            setFieldsLocalTimeZone(item, "startDateTime", "endDateTime");
        });

        return slots;
    }

    searchUsers(fullname?: string, userTypeId?: string) {
        let params = new HttpParams();

        params = !!userTypeId ? params.append("userTypeId", userTypeId) : params;
        params = !!fullname ? params.append("fullName", fullname) : params;

        return this.handleRequest(
            this.http.get<ClinicianSearchUseritem[]>(`${this.url}/filters/users`, { params }),
            this.handlersFactory.getDefault()
        );
    }

    searchPatients(): Promise<PatientSuggestion[]> {
        if(!this.appOnlineStateService.isAppOnline) {
            return Promise.resolve([]);
        }

        return this.handleRequest(
            this.http.get<PatientSuggestion[]>(`${this.url}/filters/patients`, { }),
            this.handlersFactory.getDefault()
        );
    }

    delete(id: EntityId, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<any> {
        return this.handleRequest<any>(
            this.http.delete<any>(`${this.url}/${id}`),
            this.handlersFactory.getForDelete("Slot", responseHandlerSettings)
        );
    }

    getCurrentPatientClinicians(fullname?: string): Promise<SearchUserItem[]> {
        let params = new HttpParams();
        params = !!fullname ? params.append("fullName", fullname) : params;

        return this.handleRequest(
            this.http.get<SearchUserItem[]>(`${this.url}/patient-clinicians`, { params }),
            this.handlersFactory.getDefault()
        );
    }

    cancelBooking(id: EntityId): Promise<Slot> {
        return this.handleRequest<Slot>(
            this.http.put<Slot>(`${this.url}/${id}/cancel`, null)
        );
    }

    async getAvailableSlots(queryCommand: IQueryCommand, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<Slot[]> {
        const slots = await this.handleRequest(
            this.http.get<Slot[]>(`${this.url}/available-slots`, { params: this.constructHttpParams(queryCommand) }),
            this.handlersFactory.getForReadByFilter(responseHandlerSettings)
        );

        (slots ?? []).forEach(item => {
            setFieldsLocalTimeZone(item, "startDateTime", "endDateTime");
        });

        return slots;
    }

    async getAvailableDates(queryCommand: IQueryCommand, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<Date[]> {
        const dates = await this.handleRequest(
            this.http.get<Date[]>(`${this.url}/available-dates`, { params: this.constructHttpParams(queryCommand) }),
            this.handlersFactory.getForReadByFilter(responseHandlerSettings)
        );

        (dates ?? []).forEach(item => {
            SetLocalTimeZone(<Date>item);
        });

        return dates;
    }

    isSlotAvailable(queryCommand: IQueryCommand): Promise<boolean> {
        return this.handleRequest(
            this.http.get<boolean>(`${this.url}/is-available`, { params: this.constructHttpParams(queryCommand) }),
            this.handlersFactory.getDefault()
        );
    }
}
